import React, { FunctionComponent, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { bindActionCreators } from 'redux'
import styled, { useTheme } from 'styled-components/native'

import {
  CALENDAR_SELECT_LEARN_MORE_LINK_DA,
  CALENDAR_SELECT_LEARN_MORE_LINK_OUS,
  CALENDAR_SELECT_SUBMIT_REQUEST_LINK_DA,
  CALENDAR_SELECT_SUBMIT_REQUEST_LINK_OUS,
  CALENDAR_SELECT_SUBMIT_TICKET_LINK_DA,
  CALENDAR_SELECT_SUBMIT_TICKET_LINK_OUS,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/constants'
import {
  APPOINTMENTS_CALENDAR_SUBHEADER_TEXT,
  READ_CALENDARS_BODY_TEXT,
  READ_CALENDARS_SUBHEADER_TEXT,
  SELECT_CALENDARS_TITLE_TEXT,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/messages'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  BookingMode,
  CALENDAR_CONFIGURATION_ADMIN_STATUS,
  CALENDAR_PROVIDER_ADMIN_STATUS,
  PROVIDER_CALENDAR_USAGE,
  ProviderCalendarConfiguration,
  VALID_CALENDAR_ACCESS_ROLES,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import {
  CheckboxButtonType,
  RadioButtonType,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Checkbox } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { RadioButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/RadioButtons'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryButton/TertiaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip, TOOLTIP_TRIGGER_ICON } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { ProviderCalendarLoadingModal } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/providerCalendarLoadingModal/ProviderCalendarLoadingModal'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import {
  useGetAvailableCalendarsQuery,
  useGetCalendarProviderQuery,
  usePatchCalendarProviderMutation,
  usePutAvailableCalendarsMutation,
} from '../../../calendar/data/calendarApi'
import { getAuthConfig, getAuthUserId } from '../../../data/auth/authSelectors'
import { setToastContent } from '../../../lyraTherapy/data/ltToastAutoActions'
import { getProviderDetailsCalendarState } from '../../../providers/data/providerSelectors'
import { LC_CALENDAR_ACTIVATE_SETUP, LC_CALENDAR_CURRENT, SETTINGS } from '../../constants/routingConstants'

const SelectCalendars: FunctionComponent<SelectCalendarsProps> = ({ isEdit = false, actions: { setToastContent } }) => {
  const { colors, spacing } = useTheme()
  const { formatMessage } = useIntl()
  const [readCalendarIds, setReadCalendarIds] = useState<string[]>([])
  const [appointmentCalendarId, setAppointmentCalendarId] = useState<string>('')
  const [availabilityCalendarId, setAvailabilityCalendarId] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isCalendarProviderSanitized, setIsCalendarProviderSanitized] = useState<boolean>(false)
  const navigate = useNavigate()
  const userId = useSelector(getAuthUserId)
  const config = useSelector(getAuthConfig)
  const { isDACalendarTransitionEnabled } = useFlags()
  const providerCalendarState = useSelector(getProviderDetailsCalendarState)
  const shouldShowCalendarTransition: boolean =
    isDACalendarTransitionEnabled && providerCalendarState === 'ProviderCalendarStatus.ONLINE_BOOKING_LIVE'

  const { data: calendarProvider } = useGetCalendarProviderQuery({ providerId: userId })

  const { data: calendars } = useGetAvailableCalendarsQuery(
    { providerId: userId },
    { skip: !isCalendarProviderSanitized, refetchOnMountOrArgChange: true },
  )
  const [patchCalendarProvider] = usePatchCalendarProviderMutation()
  const [putAvailableCalendars] = usePutAvailableCalendarsMutation()
  useEffect(() => {
    if (!isEdit && calendarProvider?.booking_mode === BookingMode.FREEBUSY) {
      patchCalendarProvider({
        providerId: userId,
        body: {
          admin_status: CALENDAR_PROVIDER_ADMIN_STATUS.SYNC_PENDING,
        },
      })
        .then(() => {
          setIsCalendarProviderSanitized(true)
        })
        .catch(() => {
          setToastContent({
            text: formatMessage({
              defaultMessage: 'Failed to format calendar configuration. Please try again',
              description:
                'A toast message letting the user there was an error in configuring calendar for in product calendar',
            }),
            toastType: 'error',
            id: 'SelectCalendars-setupFailure',
          })
          navigate(LC_CALENDAR_CURRENT.route)
        })
    } else {
      setIsCalendarProviderSanitized(true)
    }
  }, [formatMessage, calendarProvider?.booking_mode, isEdit, navigate, patchCalendarProvider, setToastContent, userId])

  useEffect(() => {
    if (calendars) {
      const readCalendars: string[] = []
      let appointmentCalendar: string = ''
      let availabilityCalendar: string = ''
      let primaryCalendarId: string = ''
      calendars.forEach((calendar) => {
        if (calendar.is_primary) {
          primaryCalendarId = calendar.calendar_id
        }
        if (calendar.id != null && calendar.calendar_usage == PROVIDER_CALENDAR_USAGE.READ_BUSY) {
          readCalendars.push(calendar.calendar_id)
        }
        if (calendar.id != null && calendar.calendar_usage == PROVIDER_CALENDAR_USAGE.WRITE_ALL) {
          readCalendars.push(calendar.calendar_id)
          appointmentCalendar = calendar.calendar_id
          availabilityCalendar = calendar.calendar_id
        }
        if (calendar.id != null && calendar.calendar_usage == PROVIDER_CALENDAR_USAGE.WRITE_APPOINTMENTS) {
          readCalendars.push(calendar.calendar_id)
          appointmentCalendar = calendar.calendar_id
        }
        if (calendar.id != null && calendar.calendar_usage == PROVIDER_CALENDAR_USAGE.WRITE_AVAILABILITY) {
          readCalendars.push(calendar.calendar_id)
          availabilityCalendar = calendar.calendar_id
        }
      })
      // If there are no existing calendar configs in CalendarService aka no configs with a non-null id, select the primary calendar as appointment/availability
      if (!appointmentCalendar) {
        readCalendars.push(primaryCalendarId)
        appointmentCalendar = primaryCalendarId
      }
      if (!availabilityCalendar) {
        readCalendars.push(primaryCalendarId)
        availabilityCalendar = primaryCalendarId
      }
      setReadCalendarIds(readCalendars)
      setAppointmentCalendarId(appointmentCalendar)
      setAvailabilityCalendarId(availabilityCalendar)
    }
  }, [calendars])

  const Header = styled.View(({ theme }) => ({
    flexDirection: 'row',
    padding: theme.spacing['24px'],
    justifyContent: 'space-between',
    backgroundColor: theme.colors.backgroundPrimary,
    width: '100%',
    borderBottomWidth: '1px',
    borderBottomColor: theme.colors.borderDefault,
    alignItems: 'center',
  }))

  const HeaderText = styled.View(({ theme }) => ({
    gap: theme.spacing['8px'],
  }))

  const Row = styled.View(({ theme }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['8px'],
  }))

  const RightSection = styled(Row)(({ theme }) => ({
    gap: theme.spacing['32px'],
  }))

  const ButtonGroup = styled(Row)(({ theme }) => ({
    borderLeftWidth: '1px',
    borderLeftColor: theme.colors.borderDefault,
    gap: theme.spacing['16px'],
  }))

  const PageContainer = styled.View(({ theme }) => ({
    width: 'auto',
    backgroundColor: theme.colors.backgroundSecondary,
    display: 'flex',
    alignItems: 'center',
  }))

  const SelectCalendarsContainer = styled.View({
    width: '624px',
  })

  const TitleContainer = styled(Title)(({ theme }) => ({
    paddingTop: theme.spacing['32px'],
  }))

  const CalendarsContainer = styled.View(({ theme }) => ({
    paddingTop: theme.spacing['32px'],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: theme.colors.borderDefault,
  }))

  const AvailabilityInfoContainer = styled.View(({ theme }) => ({
    backgroundColor: theme.colors.backgroundSection,
    flexDirection: 'column',
    padding: `${theme.spacing['16px']} ${theme.spacing['24px']}`,
    borderRadius: '16px',
    alignSelf: 'flex-start',
    width: '100%',
    alignItems: 'flex-start',
    marginTop: theme.spacing['24px'],
  }))

  const TitleToolTipContainer = styled.View({
    display: 'flex',
    flexDirection: 'row',
  })

  const RowContainer = styled.View(({ theme }) => ({
    width: '624px',
    height: theme.spacing['56px'],
    padding: '16px 24px 16px 0px',
    display: 'flex',
    flexDirection: 'row',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: theme.colors.borderDefault,
    backgroundColor: theme.colors.backgroundSecondary,
  }))

  const CalendarTextContainer = styled.View({
    flex: 1,
  })

  const NextButtonContainer = styled.View({
    width: '624px',
    flexDirection: 'row',
    justifyContent: 'end',
  })

  const onCheckboxChecked = (calendar_id: string) => {
    if (
      readCalendarIds.includes(calendar_id) &&
      appointmentCalendarId !== calendar_id &&
      availabilityCalendarId !== calendar_id
    ) {
      setReadCalendarIds(readCalendarIds.filter((id) => id !== calendar_id))
    } else {
      setReadCalendarIds([...readCalendarIds, calendar_id])
    }
  }

  const renderCheckboxItems = () => {
    return calendars?.map((calendar) => {
      return (
        <RowContainer key={calendar.calendar_id}>
          <CalendarTextContainer>
            <BodyText text={calendar.calendar_name} bold={calendar.is_primary} />
          </CalendarTextContainer>
          <Checkbox
            buttonType={CheckboxButtonType.CHECKBOX_DESCRIPTION_NO_OUTLINE}
            title=''
            checked={readCalendarIds.includes(calendar.calendar_id)}
            onPress={() => onCheckboxChecked(calendar.calendar_id)}
            readOnly={appointmentCalendarId === calendar.calendar_id || availabilityCalendarId === calendar.calendar_id}
            name={calendar.calendar_id}
            baseInputStyle={{ backgroundColor: colors.backgroundSecondary }}
          />
        </RowContainer>
      )
    })
  }

  const onAppointmentCalendarButtonChecked = (calendar_id: string) => {
    if (appointmentCalendarId === calendar_id) {
      setAppointmentCalendarId('')
    } else {
      setAppointmentCalendarId(calendar_id)
      if (!readCalendarIds.includes(calendar_id)) {
        setReadCalendarIds([...readCalendarIds, calendar_id])
      }
    }
  }

  const renderAppointmentCalendarItems = () => {
    return calendars?.map((calendar) => {
      if (
        ((isEdit || shouldShowCalendarTransition) && calendar.calendar_id !== appointmentCalendarId) ||
        !VALID_CALENDAR_ACCESS_ROLES.includes(calendar.access_role)
      )
        return null
      return (
        <RowContainer key={calendar.calendar_id}>
          <CalendarTextContainer>
            <BodyText text={calendar.calendar_name} bold={calendar.is_primary} />
          </CalendarTextContainer>
          {!(isEdit || shouldShowCalendarTransition) && (
            <RadioButton
              title={''}
              selected={appointmentCalendarId === calendar.calendar_id}
              value={calendar.calendar_id}
              onChange={() => onAppointmentCalendarButtonChecked(calendar.calendar_id)}
              parentContainerWidth={0}
              gridButtonsGap={0}
              buttonType={RadioButtonType.DESCRIPTION_NO_OUTLINE}
            />
          )}
        </RowContainer>
      )
    })
  }

  const handleSubmit = () => {
    const configurations: ProviderCalendarConfiguration[] = []
    calendars?.forEach((calendar) => {
      const updated_calendar: ProviderCalendarConfiguration = { ...calendar }
      if (availabilityCalendarId === updated_calendar.calendar_id) {
        if (appointmentCalendarId === updated_calendar.calendar_id) {
          updated_calendar.calendar_usage = PROVIDER_CALENDAR_USAGE.WRITE_ALL
        } else {
          updated_calendar.calendar_usage = PROVIDER_CALENDAR_USAGE.WRITE_AVAILABILITY
        }
        configurations.push(updated_calendar)
      } else if (appointmentCalendarId === updated_calendar.calendar_id) {
        updated_calendar.calendar_usage = PROVIDER_CALENDAR_USAGE.WRITE_APPOINTMENTS
        configurations.push(updated_calendar)
      } else if (readCalendarIds.includes(updated_calendar.calendar_id)) {
        updated_calendar.calendar_usage = PROVIDER_CALENDAR_USAGE.READ_BUSY
        if (isEdit && updated_calendar.admin_status !== CALENDAR_CONFIGURATION_ADMIN_STATUS.PUBLISHED) {
          updated_calendar.admin_status = CALENDAR_CONFIGURATION_ADMIN_STATUS.PUBLISHED
        }
        configurations.push(updated_calendar)
      }
    })
    if (userId) {
      setIsSaving(true)
      putAvailableCalendars({ provider_id: userId, configurations })
        .unwrap()
        .then(() => {
          if (isEdit) {
            setToastContent({
              text: formatMessage({
                defaultMessage: 'Lyra calendar updated',
                description: 'A toast message letting the user know their calendar was updated',
              }),
              toastType: 'success',
              id: 'SelectCalendars-success',
            })
            navigate(SETTINGS.route)
          } else {
            navigate(LC_CALENDAR_ACTIVATE_SETUP.route)
          }
        })
        .catch(() => {
          setToastContent({
            text: isEdit
              ? formatMessage({
                  defaultMessage: 'Failed to update calendar selections',
                  description: 'A toast message letting the user know there was an error editing calendar selections',
                })
              : formatMessage({
                  defaultMessage: 'Something went wrong. Please try again',
                  description: 'A toast message letting the user there was an error saving calendar selections',
                }),
            toastType: 'error',
            id: 'SelectCalendars-failure',
          })
          if (isEdit) {
            navigate(SETTINGS.route)
          } else {
            navigate(LC_CALENDAR_CURRENT.route)
          }
        })
        .finally(() => {
          setIsSaving(false)
        })
    }
  }

  const contactSupportLink = (
    <Link
      testID={tID('SelectCalendars-submit-ticket-link')}
      onPress={() => {
        window.open(
          config?.inProductCalendar?.useInternationalLinks
            ? CALENDAR_SELECT_SUBMIT_TICKET_LINK_OUS
            : CALENDAR_SELECT_SUBMIT_TICKET_LINK_DA,
        )
      }}
      text='Submit a ticket here.'
      underline
    />
  )

  return (
    <>
      {isEdit && (
        <Header>
          <HeaderText>
            <Title
              size={TitleSize.LARGE}
              text={
                <FormattedMessage
                  defaultMessage='Edit external calendars'
                  description='Header editing external calendars'
                />
              }
            />
          </HeaderText>
          <RightSection>
            <ButtonGroup>
              <TertiaryButton
                testID={tID('SelectCalendars-cancel-button')}
                text={<FormattedMessage defaultMessage='Cancel' description='Button to cancel calendar selection' />}
                onPress={() => {
                  navigate(SETTINGS.route)
                }}
              />
              <PrimaryButton
                testID={tID('SelectCalendars-save-button')}
                text={<FormattedMessage defaultMessage='Save' description='Button to save calendar selection' />}
                disabled={!appointmentCalendarId}
                onPress={() => {
                  handleSubmit()
                }}
              />
            </ButtonGroup>
          </RightSection>
        </Header>
      )}
      <PageContainer>
        <SelectCalendarsContainer>
          {!isEdit && (
            <TitleContainer
              size={TitleSize.LARGE}
              textAlign='center'
              text={
                <FormattedMessage
                  {...(shouldShowCalendarTransition
                    ? SELECT_CALENDARS_TITLE_TEXT.TRANSITION
                    : SELECT_CALENDARS_TITLE_TEXT.DEFAULT)}
                />
              }
            />
          )}
          {calendars?.length !== 0 && isCalendarProviderSanitized ? (
            <>
              <CalendarsContainer>
                <Title
                  size={TitleSize.SMALL}
                  text={
                    <FormattedMessage
                      {...(isEdit || shouldShowCalendarTransition
                        ? APPOINTMENTS_CALENDAR_SUBHEADER_TEXT.EDIT
                        : APPOINTMENTS_CALENDAR_SUBHEADER_TEXT.SETUP)}
                    />
                  }
                />
                <BodyText
                  color={colors.textSecondary}
                  text={
                    isEdit || shouldShowCalendarTransition ? (
                      <FormattedMessage
                        defaultMessage='We’re syncing Lyra appointments to both your external calendar and your Lyra calendar. Need help? {contact} We’ll get back to you soon.'
                        description='Body text describing outgoing calendar functionality'
                        values={{
                          contact: contactSupportLink,
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage='Select the calendar where you’d like all of your Lyra appointments to appear. We’ll sync them there and also check this calendar for any conflicts.'
                        description='Body text describing appointment calendar functionality'
                      />
                    )
                  }
                  style={{ marginBottom: spacing['24px'] }}
                />
                {renderAppointmentCalendarItems()}
              </CalendarsContainer>
              <CalendarsContainer>
                <TitleToolTipContainer>
                  <Title
                    size={TitleSize.SMALL}
                    text={
                      <FormattedMessage
                        {...(isEdit || shouldShowCalendarTransition
                          ? READ_CALENDARS_SUBHEADER_TEXT.EDIT
                          : READ_CALENDARS_SUBHEADER_TEXT.SETUP)}
                      />
                    }
                  />
                  <Tooltip
                    hoverEnabled
                    content={
                      <FormattedMessage
                        defaultMessage='Clients will not see events or details from these calendars. For your reference, they will show as “busy” on your Lyra calendar.'
                        description='Tooltip describing read busy calendar functionality'
                      />
                    }
                    triggerConfig={{
                      icon: TOOLTIP_TRIGGER_ICON.INFO,
                      size: 24,
                      fillColor: colors.textSecondary,
                    }}
                  />
                </TitleToolTipContainer>
                <BodyText
                  color={colors.textSecondary}
                  text={
                    <FormattedMessage
                      {...(isEdit || shouldShowCalendarTransition
                        ? READ_CALENDARS_BODY_TEXT.EDIT
                        : READ_CALENDARS_BODY_TEXT.SETUP)}
                    />
                  }
                  style={{ marginBottom: spacing['32px'] }}
                />
                {renderCheckboxItems()}
              </CalendarsContainer>
              {!isEdit && (
                <>
                  <AvailabilityInfoContainer>
                    <Title
                      size={TitleSize.SMALL}
                      color={colors.textSecondary}
                      text={
                        <FormattedMessage
                          defaultMessage='Where do I manage my availability for Lyra?'
                          description='Subheader text for an information section about managing Lyra availability externally'
                        />
                      }
                    />
                    <BodyText
                      color={colors.textSecondary}
                      style={{ marginTop: spacing['8px'] }}
                      text={
                        <FormattedMessage
                          defaultMessage='You can see and manage your availability on the Lyra platform. A Lyra calendar also shows up in Google, but we recommend making changes in the Lyra platform to avoid issues. {learnMore}'
                          description='Body text describing appointment calendar functionality'
                          values={{
                            learnMore: (
                              <Link
                                testID={tID('SelectCalendars-learn-more-link')}
                                onPress={() => {
                                  window.open(
                                    config?.inProductCalendar?.useInternationalLinks
                                      ? CALENDAR_SELECT_LEARN_MORE_LINK_OUS
                                      : CALENDAR_SELECT_LEARN_MORE_LINK_DA,
                                  )
                                }}
                                text='Learn More'
                                underline
                              />
                            ),
                          }}
                        />
                      }
                    />
                  </AvailabilityInfoContainer>
                  <NextButtonContainer>
                    <PrimaryButton
                      onPress={() => handleSubmit()}
                      text={<FormattedMessage defaultMessage='Next' description='Next button' />}
                      style={{ marginTop: spacing['32px'], marginBottom: spacing['32px'] }}
                      disabled={!appointmentCalendarId}
                      testID={'SelectCalendars-next-button'}
                    />
                  </NextButtonContainer>
                </>
              )}
              <BodyText
                style={{ marginTop: isEdit ? spacing['32px'] : spacing['0px'], marginBottom: spacing['32px'] }}
                color={colors.textSecondary}
                text={
                  <FormattedMessage
                    defaultMessage='Wrong Google Account? {contact} to disconnect this account and connect another one.'
                    description='A message telling user what to do if wrong Google account is displaying'
                    values={{
                      contact: (
                        <Link
                          testID={tID('SelectCalendars-submit-request-link')}
                          onPress={() => {
                            window.open(
                              config?.inProductCalendar?.useInternationalLinks
                                ? CALENDAR_SELECT_SUBMIT_REQUEST_LINK_OUS
                                : CALENDAR_SELECT_SUBMIT_REQUEST_LINK_DA,
                            )
                          }}
                          text='Submit a request'
                          underline
                        />
                      ),
                    }}
                  />
                }
              />
            </>
          ) : (
            <LoadingIndicator size={45} />
          )}
        </SelectCalendarsContainer>
      </PageContainer>
      <ProviderCalendarLoadingModal isVisible={isSaving} isCalendarSelection />
    </>
  )
}

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      setToastContent: setToastContent as any,
    },
    dispatch,
  ),
})

type SelectCalendarsProps = {
  isEdit?: boolean
  actions: {
    setToastContent: (params: Parameters<typeof setToastContent>[0]) => Promise<any>
  }
}
export default connect(null, mapDispatchToProps)(SelectCalendars)
